import { cn, tv, VariantProps } from "@utils/tw"
import { ComponentProps, forwardRef } from "react"
import { Icon, IconName } from "./Icon"

export type ChipProps = Omit<ComponentProps<"div">, "color"> &
  VariantProps<typeof chipStyles> & {
    leftIcon?: IconName
    rightIcon?: IconName
  }

export const Chip = forwardRef<HTMLDivElement, ChipProps>(
  ({ shape, variant, className, children, interactive, leftIcon, rightIcon, ...props }, ref) => {
    const styles = chipStyles({ shape, variant })
    return (
      <div className={cn(styles.base(), className)} {...props} ref={ref}>
        {leftIcon && <Icon data-part="icon" name={leftIcon} className={styles.icon()} />}
        <span data-part="label">{children}</span>
        {rightIcon && <Icon data-part="icon" name={rightIcon} className={styles.icon()} />}
      </div>
    )
  },
)

Chip.displayName = "Chip"

export const chipStyles = tv({
  base: [
    "inline-flex items-center gap-[0.3em] border-0 items-centerpy-1 whitespace-nowrap",
    "transition-colors duration-75",
    "pressable:cursor-pointer",
    "last:part-[icon]:-mr-[0.4em] first:part-[icon]:-ml-[0.4em]",
  ],
  slots: {
    icon: "size-4 shrink-0",
  },
  variants: {
    shape: {
      flat: {
        base: "type-label-caps bg-transparent min-h-[1.8em] px-[0.7em]",
      },
      rounded: {
        base: "type-label-s leading-[1.8em] rounded-sm border-none min-h-[1.9em] px-[0.75em]",
      },
    },
    variant: {
      hero: "bg-orange pressable:hover:bg-orange/90 text-black",
      black: "bg-black pressable:hover:bg-black/90 text-white",
      white: "bg-white pressable:hover:bg-white/90 text-black",
      primary: "bg-fg pressable:hover:bg-fg/90 text-bg",
      inverted: "bg-bg pressable:hover:bg-bg/90 text-fg",
      low: "bg-bg-bg pressable:hover:bg-bg-high text-fg",
      mid: "bg-bg-low pressable:hover:bg-bg-mid text-fg",
      high: "bg-bg-mid pressable:hover:bg-bg-low text-fg",
      outline: "bg-white pressable:hover:bg-white/90 text-black border border-black",
    },
    interactive: {
      true: "opacity-80 hover:opacity-100 cursor-pointer",
      false: {},
    },
  },
  defaultVariants: {
    shape: "flat",
    variant: "black",
  },
  compoundVariants: [
    {
      interactive: true,
      variant: "low",
      class: "hover:bg-bg-high opacity-100",
    },
    {
      interactive: true,
      variant: "mid",
      class: "hover:bg-bg-mid opacity-100",
    },
    {
      interactive: true,
      variant: "high",
      class: "hover:bg-bg-low opacity-100",
    },
  ],
})
